import { useEffect, useState } from 'react'
import { api } from '../api'
import { Icon } from './Icon'

/**
 * What the workspace is called, and where its site answers.
 *
 * It lived on the People screen, which is about WHO is in the workspace — so
 * somebody looking for "rename my organisation" went to Settings → the
 * organisation → General, found a list of documentation sites and a way to
 * delete everything, and had to guess that the name was kept under People.
 * The section's own sentence promised "the workspace itself"; this is the part
 * of that promise it was not keeping.
 *
 * Its own component rather than a block moved wholesale: it loads what it
 * needs and saves it, so neither screen has to hold the state for the other.
 */
export function WorkspaceIdentity({ mayChange }: { mayChange: boolean }) {
  const [workspace, setWorkspace] = useState<{
    name: string
    domain: string | null
  } | null>(null)
  const [name, setName] = useState('')
  const [domain, setDomain] = useState('')
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void api
      .workspaceSettings()
      .then((w) => {
        if (cancelled) return
        setWorkspace(w)
        setName(w.name)
        setDomain(w.domain ?? '')
      })
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
    return () => {
      cancelled = true
    }
  }, [])

  const changed =
    workspace !== null &&
    (name.trim() !== workspace.name || domain.trim() !== (workspace.domain ?? ''))

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      /**
       * Only what actually changed goes over the wire. Not an optimisation:
       * the domain is a sensitive field — changing it can demand a password
       * confirmation — and a rename that also re-sent the unchanged domain
       * would put that prompt on a typo fix.
       */
      const next = await api.updateWorkspace({
        ...(name.trim() !== workspace?.name ? { name: name.trim() } : {}),
        // An empty field means "no domain", which is a different request from
        // not mentioning one.
        ...(domain.trim() !== (workspace?.domain ?? '')
          ? { domain: domain.trim() === '' ? null : domain.trim() }
          : {}),
      })
      setWorkspace(next)
      setName(next.name)
      setDomain(next.domain ?? '')
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (!workspace) return null

  return (
    <div data-workspace-identity>
      <h2>This workspace</h2>
      <p className="home__quiet">
        What this team is called, everywhere it is named — the switcher, invitations, and the
        letters this product sends.
      </p>
      {!mayChange ? (
        <dl className="settings__facts">
          <dt>Name</dt>
          <dd>{workspace.name}</dd>
          <dt>Site domain</dt>
          <dd>{workspace.domain ?? 'none'}</dd>
        </dl>
      ) : (
        <>
          <label className="field">
            <span className="label">Name</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="What this team is called"
            />
          </label>
          <label className="field">
            <span className="label">Site domain</span>
            <input
              className="input"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="docs.example.com"
            />
            <span className="field__note">
              Where the published site answers. Recording it here is all this does — the DNS record
              and the certificate are separate, and pretending otherwise would be worse than saying
              so.
            </span>
          </label>
          {error && (
            <p className="screen__error" role="alert">
              <Icon name="alert" size={13} /> {error}
            </p>
          )}
          <div className="settings__row">
            <button
              className="btn btn--tinted btn--sm"
              data-save-workspace
              disabled={busy || !changed || name.trim().length < 2}
              onClick={() => void save()}
            >
              {saved ? 'Saved' : 'Save'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
